// Full screen for the chart and the Market Flow board, landscape on phones:
//   - where the browser allows it (Android Chrome), the element goes full screen and the screen
//     locks to landscape;
//   - where it doesn't (iPhone Safari has no element full screen and no orientation lock), the
//     element covers the screen ("pseudo" full screen) and, held upright, asks to turn the phone
//     sideways. It isn't rotated in CSS: the chart maps touches to the unrotated screen, so the
//     crosshair and panning would land in the wrong place.
// Leaving full screen unlocks the orientation.
import { useCallback, useEffect, useRef, useState } from 'react';
import { SmartphoneIcon } from 'lucide-react';

const isPhone = () => matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600;

type Lockable = ScreenOrientation & { lock?: (o: 'landscape') => Promise<void>; unlock?: () => void };

export function useFullscreen<T extends HTMLElement>(onChange?: () => void) {
  const ref = useRef<T>(null);
  const [native, setNative] = useState(false); // the browser's own full screen
  const [pseudo, setPseudo] = useState(false); // covering the screen ourselves (iPhone)
  const [portrait, setPortrait] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(orientation: portrait)').matches);
  const changed = useRef(onChange);
  changed.current = onChange;

  useEffect(() => {
    const onFs = () => setNative(document.fullscreenElement === ref.current && !!ref.current);
    const mq = matchMedia('(orientation: portrait)');
    const onOrient = () => setPortrait(mq.matches);
    document.addEventListener('fullscreenchange', onFs);
    mq.addEventListener('change', onOrient);
    return () => { document.removeEventListener('fullscreenchange', onFs); mq.removeEventListener('change', onOrient); };
  }, []);
  const fs = native || pseudo;
  // The chart refits to its new size after every change (full screen, the phone turning).
  useEffect(() => { const t = setTimeout(() => changed.current?.(), 60); return () => clearTimeout(t); }, [fs, portrait]);
  // Leaving the browser's full screen (Esc, the back gesture) also unlocks the orientation.
  useEffect(() => {
    if (!native) try { (screen.orientation as Lockable | undefined)?.unlock?.(); } catch { /* not locked */ }
  }, [native]);
  // Pseudo full screen: Esc leaves it, and the page behind doesn't scroll.
  useEffect(() => {
    if (!pseudo) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPseudo(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [pseudo]);

  const enter = useCallback(async () => {
    const el = ref.current;
    if (!el) return;
    let ok = false;
    if (el.requestFullscreen) {
      try { await el.requestFullscreen(); ok = true; } catch { /* not allowed here */ }
    }
    if (!ok) { setPseudo(true); return; }
    if (isPhone()) {
      try { await (screen.orientation as Lockable).lock?.('landscape'); } catch { /* the hint covers it */ }
    }
  }, []);
  const exit = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    setPseudo(false);
  }, []);
  const toggle = useCallback(() => { if (fs) exit(); else void enter(); }, [fs, enter, exit]);

  return {
    ref, fs, toggle, exit,
    /** Classes for the element: covering the screen in pseudo full screen. */
    cover: pseudo ? 'fixed inset-0 z-[60] overflow-auto pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]' : '',
    /** Full screen on a phone held upright: show "turn your phone sideways". */
    turnHint: fs && portrait && isPhone(),
  };
}

/** Full screen on a phone held upright: the one line that asks to turn it sideways. */
export function TurnHint() {
  return (
    <div role="status" className="mb-2 flex items-center gap-2 rounded-lg bg-brand-soft px-3 py-2 text-[13px] font-medium text-brand-strong">
      <SmartphoneIcon className="size-4 rotate-90" />Turn your phone sideways for the full-screen view
    </div>
  );
}

/** Where floating menus (dropdowns, selects, popovers, tooltips, dialogs) open: inside the element in
 * the browser's own full screen (outside it they wouldn't show), else the page. */
export function portalContainer(): HTMLElement | undefined {
  return (typeof document !== 'undefined' && (document.fullscreenElement as HTMLElement | null)) || undefined;
}
